// DateTimePicker/DateTimePicker.tsx

import { useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { WhellDrumColumn } from './WhellDrumColumn'

import type { DateTimePickerProps } from './types'

import { combineToLocalDate, combineToUTC, DAYS, HOURS, MINUTES, toFarsiNum, toMs } from './utils'

export function DateTimePicker({
  value,
  onChange,
  confirmText = 'SUBMIT',
  hidePastTimes = false,
  minuteStep = 1,
  setOpen,
}: DateTimePickerProps) {
  const { t } = useTranslation()

  const safeMinuteStep = Number.isFinite(minuteStep) && minuteStep > 0 ? minuteStep : 1

  /*
   * Minutes based on step.
   *
   * Example:
   * minuteStep = 15
   *
   * 00
   * 15
   * 30
   * 45
   */
  const minuteChoices = useMemo(() => {
    return MINUTES.filter(minute => minute.value % safeMinuteStep === 0)
  }, [safeMinuteStep])

  const lastMinute = minuteChoices[minuteChoices.length - 1]?.value ?? 0

  /*
   * Day
   */
  const initialDayIndex = Math.max(
    0,
    DAYS.findIndex(day => day.value === value?.day),
  )

  const [dayIndex, setDayIndex] = useState(initialDayIndex)

  const selectedDay = DAYS[dayIndex]?.value ?? DAYS[0]?.value ?? ''

  /*
   * Hours
   */
  const hourOptions = useMemo(() => {
    if (!hidePastTimes) {
      return HOURS
    }

    const now = Date.now()

    const filtered = HOURS.filter(hour => {
      return toMs(combineToUTC(selectedDay, hour.value, lastMinute)) >= now
    })

    /*
     * Never return an empty wheel.
     */
    return filtered.length > 0 ? filtered : HOURS
  }, [hidePastTimes, selectedDay, lastMinute])

  const initialHour = value?.hour ?? new Date().getHours()

  const initialHourIndex = Math.max(
    0,
    hourOptions.findIndex(hour => hour.value === initialHour),
  )

  const [hourIndex, setHourIndex] = useState(initialHourIndex)

  const selectedHour = hourOptions[hourIndex]?.value ?? hourOptions[0]?.value ?? 0

  /*
   * Minutes
   */
  const minuteOptions = useMemo(() => {
    if (!hidePastTimes) {
      return minuteChoices
    }

    const now = Date.now()

    const filtered = minuteChoices.filter(minute => {
      return toMs(combineToUTC(selectedDay, selectedHour, minute.value)) >= now
    })

    return filtered.length > 0 ? filtered : minuteChoices
  }, [hidePastTimes, minuteChoices, selectedDay, selectedHour])

  const initialMinute = value?.minute ?? 0

  const initialMinuteIndex = Math.max(
    0,
    minuteOptions.findIndex(minute => minute.value === initialMinute),
  )

  const [minuteIndex, setMinuteIndex] = useState(initialMinuteIndex)

  /*
   * Keep indexes valid when options change.
   *
   * This is especially important when:
   *
   * today -> tomorrow
   *
   * because the available hours/minutes
   * can change.
   */
  useEffect(() => {
    if (hourIndex >= hourOptions.length) {
      setHourIndex(Math.max(0, hourOptions.length - 1))
    }
  }, [hourIndex, hourOptions.length])

  useEffect(() => {
    if (minuteIndex >= minuteOptions.length) {
      setMinuteIndex(Math.max(0, minuteOptions.length - 1))
    }
  }, [minuteIndex, minuteOptions.length])

  /*
   * Current value
   */
  const currentValue = useMemo(() => {
    const day = DAYS[dayIndex]?.value ?? DAYS[0]?.value ?? ''

    const hour = hourOptions[hourIndex]?.value ?? hourOptions[0]?.value ?? 0

    const minute = minuteOptions[minuteIndex]?.value ?? minuteOptions[0]?.value ?? 0

    return {
      day,
      hour,
      minute,
      utc: combineToLocalDate(day, hour, minute),
    }
  }, [dayIndex, hourIndex, minuteIndex, hourOptions, minuteOptions])

  /*
   * Day changed.
   */
  const handleDaySelect = (index: number) => {
    setDayIndex(index)

    /*
     * Find first available hour
     * instead of blindly using index 0.
     */
    setHourIndex(0)
    setMinuteIndex(0)
  }

  /*
   * Hour changed.
   */
  const handleHourSelect = (index: number) => {
    setHourIndex(index)

    /*
     * Minute options can change
     * depending on the selected hour.
     */
    setMinuteIndex(0)
  }

  /*
   * Confirm.
   */
  const handleConfirm = () => {
    onChange?.(currentValue)
    setOpen(false)
  }

  return (
    <div className="w-full shrink-0">
      {/* Wheels */}

      <div
        className="flex w-full flex-row-reverse items-center justify-center gap-4 px-4 py-5"
        dir="rtl"
      >
        {/* Minute */}

        <WhellDrumColumn
          items={minuteOptions}
          selectedIndex={minuteIndex}
          onSelect={setMinuteIndex}
          label="دقیقه"
        />

        <span className="mt-6 px-1 text-lg font-light text-muted-foreground">:</span>

        {/* Hour */}

        <WhellDrumColumn
          items={hourOptions}
          selectedIndex={hourIndex}
          onSelect={handleHourSelect}
          label="ساعت"
        />

        {/* Day */}

        <WhellDrumColumn
          items={DAYS}
          selectedIndex={dayIndex}
          onSelect={handleDaySelect}
          label="روز"
        />
      </div>

      {/* Preview */}

      <p className="mb-4 text-center text-sm text-gray-500" dir="rtl">
        {DAYS[dayIndex]?.label}
        {' · '}
        {toFarsiNum(currentValue.hour)}:{toFarsiNum(currentValue.minute)}
      </p>

      {/* Confirm */}

      <div className="p-4">
        <button
          type="button"
          className="btn btn-primary btn-lg flex w-full items-center justify-center"
          onClick={handleConfirm}
        >
          {t(confirmText)}
        </button>
      </div>
    </div>
  )
}
